import React from 'react';
import { createGlobalStyle, ThemeProvider as StyledThemeProvider } from 'styled-components';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import {
  StyledEngineProvider,
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import componentColor, { dls } from './compat/colors';
import { makeTheme } from './compat/theme';

import { AuthenticationProvider } from './context/Authentication';
import { IntlProvider } from './context/IntlProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import LoadingIndicator from './components/LoadingIndicator';
import PrivateRoute from './components/PrivateRoute';
import { PREPAIRING_ENABLED } from './pages/prePairing/prePairing.flag';

import { getRealmFromHostname } from './api';

// lazy imports for pages to enable bundle code splitting per route
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const WelcomePage = React.lazy(() => import('./pages/WelcomePage'));
const RealmLoginRedirect = React.lazy(() => import('./pages/RealmLoginRedirect'));
const AuthorizePage = React.lazy(() => import('./pages/AuthorizePage'));
const NewPatientsPage = React.lazy(() => import('./pages/NewPatientsPage'));
const AddPatientPage = React.lazy(() => import('./pages/AddPatientPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const PrePairingPage = React.lazy(() => import('./pages/PrePairingPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const TenantNotFoundPage = React.lazy(() => import('./pages/TenantNotFoundPage'));

export const GlobalStyle = createGlobalStyle`
  html {
    box-sizing: border-box;
    height: 100%;
    font-size: 16px;
  }
  *, *:before, *:after {
    box-sizing: inherit;
  }
  * {
    font-family: CentraleSans, sans-serif;
  }

  body {
    height: 100%;
    margin: 0;
    padding: 0;
    background-color: ${componentColor(dls.listItem.background.default.hover)};
  }

  #root {
    height: 100%;
  }
`;

const defaultTheme = {
  color: 'group-blue',
  tone: 'ultra-light',
  accent: 'orange',
  navigation: 'bright',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const theme = createTheme({ ...makeTheme(defaultTheme as any)() } as any);

// On org-specific hostnames (hospital-a.dev.qarescan.com) show the login form.
// On generic hostnames show the welcome/how-to-access page.
const LoginRoute: React.FC = () => (getRealmFromHostname() ? <LoginPage /> : <WelcomePage />);

const App: React.FC = () => {
  return (
    <StyledEngineProvider injectFirst>
      <ErrorBoundary>
        <GlobalStyle />
        <MuiThemeProvider theme={theme}>
          <StyledThemeProvider theme={theme}>
            <AuthenticationProvider>
              <IntlProvider>
                <BrowserRouter>
                  <React.Suspense fallback={<LoadingIndicator />}>
                    <Routes>
                      <Route path="/login" element={<LoginRoute />} />
                      <Route path="/login/:realm" element={<RealmLoginRedirect />} />
                      <Route path="/authorize" element={<AuthorizePage />} />
                      <Route path="/logout/callback" element={<Navigate to="/login" replace />} />
                      <Route path="/" element={<Navigate to="/patients" replace />} />
                      <Route element={<PrivateRoute />}>
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/patients" element={<NewPatientsPage />} />
                        <Route
                          element={<PrivateRoute requirePairingAccess redirectTo="/patients" />}
                        >
                          <Route path="/patients/add" element={<AddPatientPage />} />
                          {PREPAIRING_ENABLED && (
                            <Route path="/prepairing" element={<PrePairingPage />} />
                          )}
                        </Route>
                      </Route>
                      <Route path="/tenant-not-found" element={<TenantNotFoundPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </React.Suspense>
                </BrowserRouter>
              </IntlProvider>
            </AuthenticationProvider>
          </StyledThemeProvider>
        </MuiThemeProvider>
      </ErrorBoundary>
    </StyledEngineProvider>
  );
};

export default App;
